import {
  CPR_PLACEHOLDER_TOKENS,
  buildPlaceholderDictionary,
  fillPlaceholderText,
  reportPlaceholderContext,
} from '@/lib/patientReports/placeholders';
import {
  resolveReportPreview,
  type ReportPreviewModel,
  type ReportSection,
} from '@/lib/patientReports/template';

export type PersonalTextFill = {
  /** type id -> body, only when this user has exactly one text of that type. */
  bodies: ReadonlyMap<string, string>;
  /** saved text id -> body. Used when a section names a specific text. */
  byId?: ReadonlyMap<string, string>;
  /** Types where this user has more than one saved text. */
  manyTypeIds?: ReadonlySet<string>;
  authorName: string;
  clinicName: string;
  generatedAt: Date;
  finishedAt?: Date | null;
  completed?: boolean;
  missingText: string;
  chooseText: string;
  /** Shown when the type has several texts and the section has not chosen one. */
  pickText?: string;
};

type PreviewInput = Parameters<typeof resolveReportPreview>[0];

/**
 * Resolve a report, then fill personal texts with the same placeholder dictionary
 * the Word template uses. Personal texts are omitted from that dictionary's
 * [Indhold], so a text cannot include itself.
 */
export function resolvePreviewWithPersonalTexts(
  input: PreviewInput,
  fill: PersonalTextFill,
): ReportPreviewModel {
  const shell = resolveReportPreview({
    ...input,
    blankUserText: true,
  });
  const dictionary = buildPlaceholderDictionary(
    reportPlaceholderContext({
      reportTitle: input.title,
      authorName: fill.authorName,
      clinicName: fill.clinicName,
      generatedAt: fill.generatedAt,
      finishedAt: fill.finishedAt ?? null,
      completed: fill.completed === true,
      patient: input.catalog.patient,
      cprAccess: input.cprAccess,
      cpr: input.cpr,
      cprHidden: input.labels.cprHidden,
      cprLoading: input.labels.cprLoading,
      coverTitle: input.coverTitle,
      coverBody: input.coverBody,
      sections: input.sections,
      model: shell,
    }),
  );
  return resolveReportPreview({
    ...input,
    userTexts: fill.bodies,
    userTextById: fill.byId,
    manyUserTextTypeIds: fill.manyTypeIds,
    fillText: (body) => fillPlaceholderText(body, dictionary),
    missingUserText: input.showEmpty ? fill.missingText : '',
    chooseUserText: input.showEmpty ? fill.chooseText : '',
    pickUserText: input.showEmpty ? (fill.pickText ?? fill.chooseText) : '',
  });
}

export function personalTextBodies(
  texts: readonly { typeId: string; body: string }[],
): Map<string, string> {
  const counts = new Map<string, number>();
  for (const text of texts) counts.set(text.typeId, (counts.get(text.typeId) ?? 0) + 1);
  const bodies = new Map<string, string>();
  for (const text of texts) {
    if (counts.get(text.typeId) === 1) bodies.set(text.typeId, text.body);
  }
  return bodies;
}

export function personalTextById(
  texts: readonly { id: string; body: string }[],
): Map<string, string> {
  const bodies = new Map<string, string>();
  for (const text of texts) bodies.set(text.id, text.body);
  return bodies;
}

/** Tags where this user already has more than one saved text. */
export function personalTextManyTypeIds(texts: readonly { typeId: string }[]): Set<string> {
  const counts = new Map<string, number>();
  for (const text of texts) counts.set(text.typeId, (counts.get(text.typeId) ?? 0) + 1);
  const many = new Set<string>();
  for (const [typeId, count] of counts) {
    if (count > 1) many.add(typeId);
  }
  return many;
}

/** A single-version tag cannot be chosen again once this user has a text with it. */
export function textTypeTaken(
  type: { id: string; singleVersion?: boolean },
  texts: readonly { typeId: string }[],
): boolean {
  return type.singleVersion === true && texts.some((text) => text.typeId === type.id);
}

/** Texts already placed on the report, including an older section that only names the type. */
export function insertedPersonalTextIds(
  sections: readonly { kind: string; typeId?: string | null; textId?: string | null }[],
  texts: readonly { id: string; typeId: string }[],
): Set<string> {
  const counts = new Map<string, number>();
  for (const text of texts) counts.set(text.typeId, (counts.get(text.typeId) ?? 0) + 1);
  const used = new Set<string>();
  for (const section of sections) {
    if (section.kind !== 'user_text') continue;
    if (section.textId) {
      used.add(section.textId);
      continue;
    }
    if (!section.typeId || counts.get(section.typeId) !== 1) continue;
    const only = texts.find((text) => text.typeId === section.typeId);
    if (only) used.add(only.id);
  }
  return used;
}

export function personalTextChoiceLabel(body: string, emptyLabel: string): string {
  const line = body.replace(/\s+/g, ' ').trim();
  if (!line) return emptyLabel;
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
}

/** CPR is fetched only when a personal text on this report actually contains the token. */
export function personalTextNeedsCpr(
  sections: readonly ReportSection[],
  bodies: ReadonlyMap<string, string>,
  byId: ReadonlyMap<string, string> = new Map(),
): boolean {
  return sections.some((section) => {
    if (section.kind !== 'user_text') return false;
    const body = section.textId
      ? (byId.get(section.textId) ?? '')
      : section.typeId
        ? (bodies.get(section.typeId) ?? '')
        : '';
    return CPR_PLACEHOLDER_TOKENS.some((token) => body.includes(token));
  });
}
